import { useState, useEffect, useRef, useMemo, use } from 'react';
import { usePlacesData } from '@/data/places/places-data-context';
import { HistoricalEventsByBookDataContext } from '@/data/historical-events/historical-events-by-book-data-context';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { getStandardPeriod, PERIOD_DEFINITIONS } from '@/lib/utils/historical-periods-shared';
import defaultHeroImage from '@/assets/hero7.png';

/**
 * Hook to get the composition place image for a passage's book
 * Uses the composition.region or composition.relatedPlaces from HistoricalEventsByBook
 * Returns the era-specific reconstruction image for the place where the book was written
 */
export function useCompositionPlaceImage(passageId: string | null) {
  const { getPlaceByName } = usePlacesData();
  const historicalEventsByBookContext = use(HistoricalEventsByBookDataContext);
  const passageContext = use(PassageDataContext);
  const [isReady, setIsReady] = useState(false);
  const currentPassageRef = useRef<string | null>(null);

  // Synchronously get image URL from hydrated data
  const imageData = useMemo(() => {
    if (!passageId) {
      return {
        reconstructionImageUrl: defaultHeroImage,
        placeName: null,
        hasImage: false
      };
    }

    // Check if passage data is loaded
    if (!passageContext.data) {
      return {
        reconstructionImageUrl: defaultHeroImage,
        placeName: null,
        hasImage: false
      };
    }

    // Get the passage to find the book
    const passage = passageContext.get(passageId);
    if (!passage) {
      return {
        reconstructionImageUrl: defaultHeroImage,
        placeName: null,
        hasImage: false
      };
    }

    // Get the historical events by book data for this book
    const bookData = historicalEventsByBookContext?.getHistoricalEventsByBookByBook(passage.book);
    if (!bookData?.composition) {
      return {
        reconstructionImageUrl: defaultHeroImage,
        placeName: null,
        hasImage: false
      };
    }

    // Try to find a place using the fallback strategy:
    // 1. First try composition.region
    // 2. If not found, try first matching place from composition.relatedPlaces
    let place = null;
    let placeName = null;

    // Strategy 1: Try region
    if (bookData.composition.region) {
      place = getPlaceByName(bookData.composition.region);
      if (place) {
        placeName = bookData.composition.region;
      }
    }

    // Strategy 2: Try relatedPlaces
    if (!place && bookData.composition.relatedPlaces) {
      for (const relatedPlace of bookData.composition.relatedPlaces) {
        place = getPlaceByName(relatedPlace);
        if (place) {
          placeName = relatedPlace;
          break;
        }
      }
    }

    // If we found a place, get the era-specific reconstruction image
    if (place && place.images?.reconstructionsByPeriod) {
      // Get the composition date to determine the era
      const compositionYear = bookData.composition.compositionDate?.startYear;

      // Find the matching historical period
      let selectedPeriod: HistoricalPeriod | undefined;
      if (compositionYear !== null && compositionYear !== undefined) {
        selectedPeriod = getStandardPeriod(compositionYear);
      }

      // Get the reconstruction for the selected period
      const reconstructionsByPeriod = place.images.reconstructionsByPeriod;
      const availablePeriods = Object.keys(reconstructionsByPeriod) as HistoricalPeriod[];

      // If we have a matching period and it has an image, use it
      if (selectedPeriod && reconstructionsByPeriod[selectedPeriod]?.imageUrl) {
        return {
          reconstructionImageUrl: reconstructionsByPeriod[selectedPeriod]!.imageUrl,
          placeName,
          hasImage: true
        };
      }

      // Fallback: Use the most recent period (likely most detailed)
      if (availablePeriods.length > 0) {
        const sortedPeriods = availablePeriods.sort((a, b) => {
          const aEnd = PERIOD_DEFINITIONS[a]?.end || 0;
          const bEnd = PERIOD_DEFINITIONS[b]?.end || 0;
          return bEnd - aEnd; // Descending order (most recent first)
        });

        const fallbackImageUrl = reconstructionsByPeriod[sortedPeriods[0]]?.imageUrl;
        if (fallbackImageUrl) {
          return {
            reconstructionImageUrl: fallbackImageUrl,
            placeName,
            hasImage: true
          };
        }
      }
    }

    return {
      reconstructionImageUrl: defaultHeroImage,
      placeName: null,
      hasImage: false
    };
  }, [passageId, passageContext, historicalEventsByBookContext, getPlaceByName]);

  // Preload image in the background (async)
  useEffect(() => {
    // Reset ready state when passage changes
    if (currentPassageRef.current !== passageId) {
      setIsReady(false);
      currentPassageRef.current = passageId;
    }

    // If no image exists, mark as ready immediately (default image is always available)
    if (!imageData.hasImage) {
      setIsReady(true);
      return;
    }

    // Preload the actual image file in the background
    const loadImage = async () => {
      try {
        const img = new Image();
        img.src = imageData.reconstructionImageUrl;

        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject();
          setTimeout(() => reject(new Error('Image load timeout')), 10000);
        });

        setIsReady(true); // Image successfully preloaded
      } catch (error) {
        console.error('Error preloading composition place image:', error);
        setIsReady(true); // Mark as ready anyway - we have the URL even if preload failed
      }
    };

    loadImage();
  }, [imageData]);

  return {
    reconstructionImageUrl: imageData.reconstructionImageUrl,
    placeName: imageData.placeName,
    isReady
  };
}

